import { useState, useEffect } from "react";
import { usePortfolioSize } from "./portfolio/PortfolioSizeContext";
import { calculateRiskScore, getRiskProfile } from "@/lib/risk-utils";
import { AssetClass } from "@/types/portfolio";
import AssetAllocationHeader from "./asset-allocation/AssetAllocationHeader";
import AssetAllocationList from "./asset-allocation/AssetAllocationList";
import AssetAllocationTotal from "./asset-allocation/AssetAllocationTotal";
import AssetAllocationChart from "./asset-allocation/AssetAllocationChart";
import RiskScoreCard from "./asset-allocation/RiskScoreCard";
import ContinueButton from "./asset-allocation/ContinueButton";
import { defaultAssetClasses } from "./alts-allocation/defaultAllocations";

interface AssetAllocationStepProps {
  onContinue: () => void;
}

const AssetAllocationStep = ({ onContinue }: AssetAllocationStepProps) => {
  const { portfolioSize } = usePortfolioSize();
  const [assetClasses, setAssetClasses] = useState<AssetClass[]>(defaultAssetClasses);

  useEffect(() => {
    const savedAllocations = localStorage.getItem('assetAllocations');
    if (savedAllocations) {
      const parsed = JSON.parse(savedAllocations);
      // Preserve original colors while loading saved values
      setAssetClasses(prev => prev.map(asset => ({
        ...asset,
        value: parsed.find((p: AssetClass) => p.name === asset.name)?.value ?? asset.value
      })));
    }
  }, []);

  const handleSliderChange = (index: number, newValue: number) => {
    const total = assetClasses.reduce((sum, asset, i) => 
      i !== index ? sum + asset.value : sum, 0
    ) + newValue;

    if (total <= 100) {
      const updatedAssetClasses = assetClasses.map((asset, i) => 
        i === index ? { ...asset, value: newValue } : asset
      );
      setAssetClasses(updatedAssetClasses);
      localStorage.setItem('assetAllocations', JSON.stringify(updatedAssetClasses));
    }
  };

  const handleInputChange = (index: number, value: string) => {
    const numValue = value === "" ? 0 : Number(value.replace(/[^0-9]/g, ""));
    if (!isNaN(numValue)) {
      handleSliderChange(index, numValue);
    }
  };

  const handleInputBlur = (index: number) => {
    const total = assetClasses.reduce((sum, asset) => sum + asset.value, 0);
    if (total > 100) {
      const excess = total - 100;
      const currentValue = assetClasses[index].value;
      handleSliderChange(index, Math.max(0, currentValue - excess));
    }
  };

  const getCurrentAllocations = () => {
    const allocations = {
      stocks: 0,
      bonds: 0,
      cash: 0,
      alternatives: 0
    };

    assetClasses.forEach(asset => {
      const key = asset.name.toLowerCase() as keyof typeof allocations;
      allocations[key] = asset.value;
    });

    return allocations;
  };

  const totalAllocation = assetClasses.reduce((sum, asset) => sum + asset.value, 0);
  const riskScore = calculateRiskScore(getCurrentAllocations());
  const riskProfile = getRiskProfile(riskScore).label;

  const handleContinue = () => {
    localStorage.setItem('assetAllocations', JSON.stringify(assetClasses));
    onContinue();
  };

  return (
    <div className="space-y-4">
      <AssetAllocationHeader />
      
      <AssetAllocationList
        assetClasses={assetClasses}
        portfolioSize={portfolioSize}
        onSliderChange={handleSliderChange}
        onInputChange={handleInputChange}
        onInputBlur={handleInputBlur}
      />

      <AssetAllocationTotal totalAllocation={totalAllocation} />

      <div className="h-[200px] md:h-[250px]">
        <AssetAllocationChart assetClasses={assetClasses} />
      </div>

      <RiskScoreCard riskScore={riskScore} riskProfile={riskProfile} />

      <ContinueButton 
        onContinue={handleContinue}
        disabled={totalAllocation !== 100}
      />
    </div>
  );
};

export default AssetAllocationStep;